<template>
    <div :id="blok.anchor" class="ui-simple-title" :class="componentClasses" data-test="ui-simple-title">
        <component
            :is="blok.headingType ? blok.headingType : 'h2'"
            :class="classes"
            :style="[fontColorSettings(blok.font?.[0]), borderColorSettings(blok.font?.[0])]"
        >
            <span>{{ blok.text }}</span>
        </component>
    </div>
</template>

<script setup lang="ts">
import type { ICoreSimpleTitle } from '@nuxt-monorepo/ui/types/components/CoreSimpleTitle';
import {
    fontClasses,
    fontColorSettings,
    borderColorSettings,
    borderSettings,
    dividerSettings,
} from '@nuxt-monorepo/ui/utils/fontClasses';

const { blok } = defineProps<{ blok: ICoreSimpleTitle }>();

const classes = computed(() => fontClasses(blok, blok.mobileBreakpoint || 'md', true));

const componentClasses = [
    blok.mobileFont?.[0]?.fontSize || blok.font?.[0]?.fontSize ? '' : 'no-font-size',
    ...borderSettings(blok.mobileFont?.[0], blok.font?.[0], blok.mobileBreakpoint || 'md'),
    ...dividerSettings(blok.mobileFont?.[0], blok.font?.[0], blok.mobileBreakpoint || 'md'),
    borderColorSettings(blok.font?.[0]),
];
</script>

<style scoped lang="postcss">
.ui-simple-title {
    &.no-font-size {
        h1 {
            @apply text-4xl;
        }
        h2 {
            @apply text-4xl;
        }
        h3 {
            @apply text-3xl;
        }
        h4 {
            @apply text-2xl;
        }
        h5 {
            @apply text-xl;
        }
        h6 {
            @apply text-lg;
        }
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            @apply leading-[normal];
        }
    }

    &.has-underline-border {
        @apply border-b-[1px];
    }

    &.has-divider {
        @apply relative;
        &:before {
            @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
        }
    }

    @screen md {
        &.md\:has-underline-border {
            @apply border-b-[1px];
            &.has-divider {
                @apply before:hidden;
            }
        }
        &.md\:has-divider {
            @apply relative;
            &:before {
                @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
            }
            &.has-underline-border {
                @apply border-b-0;
            }
        }
    }

    @screen lg {
        &.lg\:has-underline-border {
            @apply border-b-[1px];
            &.has-divider {
                @apply before:hidden;
            }
        }
        &.lg\:has-divider {
            @apply relative;
            &:before {
                @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
            }
            &.has-underline-border {
                @apply border-b-0;
            }
        }
    }

    @screen xl {
        &.xl\:has-underline-border {
            @apply border-b-[1px];
            &.has-divider {
                @apply before:hidden;
            }
        }
        &.xl\:has-divider {
            @apply relative;
            &:before {
                @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
            }
            &.has-underline-border {
                @apply border-b-0;
            }
        }
    }
}
</style>
